export type InvestigationStatusDto = 'DRAFT' | 'OPEN' | 'PAUSED' | 'CLOSED' | 'ARCHIVED';
export type InvestigationClosureOutcomeDto = 'RESOLVED' | 'HANDED_OFF' | 'UNABLE_TO_CONFIRM' | 'NO_LONGER_RELEVANT';

export type MachineKnowledgeTypeDto = 'CONFIRMED_CAUSE' | 'CONFIRMED_FINDING' | 'CORRECTIVE_ACTION' | 'VERIFIED_OUTCOME' | 'RULED_OUT_CAUSE';

export interface MachineKnowledgeCandidateDto {
  candidate_id: string;
  suggested_type: MachineKnowledgeTypeDto;
  allowed_types: MachineKnowledgeTypeDto[];
  statement: string;
  source_kind: string;
  source_id: string;
  supporting_evidence: Array<{ artifact_type: string; artifact_id: string; document_id?: string | null; source_locator?: Record<string, unknown> | null }>;
}

export interface MachineKnowledgeCandidatesResponseDto {
  schema_version: string;
  investigation_id: string;
  closure_outcome: InvestigationClosureOutcomeDto;
  requires_review: boolean;
  candidates: MachineKnowledgeCandidateDto[];
  request_id?: string;
}

export interface MachineKnowledgeConfirmationDto {
  candidate_id: string;
  knowledge_type: MachineKnowledgeTypeDto;
  statement: string;
}

export interface MachineKnowledgeRecordDto {
  record_id: string;
  knowledge_id: string;
  knowledge_type: MachineKnowledgeTypeDto;
  statement: string;
  investigation_id: string;
  closure_timestamp: string;
  confirmed_by_user_id: string;
  reasoning_state_version?: number | null;
  supporting_evidence: Array<{ artifact_type: string; artifact_id: string; document_id?: string | null; source_locator?: Record<string, unknown> | null }>;
  closure_source?: {
    closure_outcome?: string | null;
    source_kind?: string | null;
    source_text?: string | null;
    source_id?: string | null;
  };
  authority: string;
  structuring_method?: string | null;
  status: 'ACTIVE' | 'REVIEW_REQUIRED' | 'SUPERSEDED' | 'RETRACTED' | string;
  version: number;
  supersedes_record_id?: string | null;
  superseded_by_record_id?: string | null;
  lifecycle_changed_at?: string | null;
  lifecycle_reason?: string | null;
}

export interface InvestigationClosureDto {
  outcome: InvestigationClosureOutcomeDto;
  what_happened: string;
  what_learned: string;
  what_was_done: string;
  what_happened_afterward: string;
  follow_up: string;
  closed_by_user_id: string;
  closed_at?: string | null;
}

export interface InvestigationClosureInputDto {
  outcome: InvestigationClosureOutcomeDto;
  what_happened: string;
  what_learned?: string;
  what_was_done?: string;
  what_happened_afterward?: string;
  follow_up?: string;
  machine_knowledge_reviewed?: boolean;
  machine_knowledge_confirmations?: MachineKnowledgeConfirmationDto[];
}
export type InvestigationRoleDto = 'OWNER' | 'EDITOR' | 'CONTRIBUTOR' | 'VIEWER';
export type InvestigationContentTypeDto =
  | 'USER_HYPOTHESIS'
  | 'AI_SUGGESTED_HYPOTHESIS'
  | 'EVIDENCE_BACKED_FINDING'
  | 'CONFLICTING_EVIDENCE'
  | 'REJECTED_HYPOTHESIS'
  | 'USER_NOTE'
  | 'OPEN_QUESTION'
  | 'FINAL_CONCLUSION';

export interface InvestigationDto {
  investigation_id: string;
  customer_id: string;
  machine_id: string;
  title: string;
  objective: string;
  owner_user_id: string;
  status: InvestigationStatusDto;
  participant_user_ids: string[];
  participant_roles: Record<string, InvestigationRoleDto>;
  source_conversation_id?: string | null;
  conversation_ids: string[];
  closure?: InvestigationClosureDto | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface InvestigationMemoryItemDto {
  content_id?: string;
  content_type?: InvestigationContentTypeDto | string;
  text: string;
  created_by_user_id?: string;
  created_at?: string;
  supporting_evidence_ids?: string[];
}

export interface InvestigationMemoryDto {
  investigation_id: string;
  customer_id: string;
  machine_id: string;
  working_hypotheses: InvestigationMemoryItemDto[];
  findings: InvestigationMemoryItemDto[];
  conflicting_evidence: InvestigationMemoryItemDto[];
  rejected_hypotheses: InvestigationMemoryItemDto[];
  open_questions: string[];
  notes: InvestigationMemoryItemDto[];
  conclusions: InvestigationMemoryItemDto[];
  current_filters: Record<string, unknown>;
  updated_at?: string | null;
}

export interface PinnedEvidenceDto {
  evidence_id: string;
  artifact_type: string;
  artifact_id: string;
  pinned_by_user_id: string;
  pinned_at: string;
  note?: string | null;
}

export interface InvestigationContentDto {
  content_id: string;
  content_type: InvestigationContentTypeDto;
  text: string;
  created_by_user_id: string;
  created_at: string;
  supporting_evidence_ids: string[];
}

export interface InvestigationActivityDto {
  activity_id: string;
  investigation_id: string;
  user_id: string;
  activity_type: string;
  created_at: string;
  payload: Record<string, unknown>;
}

export interface ListInvestigationsResponseDto {
  schema_version: string;
  machine_id: string;
  investigations: InvestigationDto[];
  count: number;
  request_id?: string;
}


export type InvestigationSynthesisStatusDto = 'MATERIAL_CHANGE' | 'NO_MATERIAL_CHANGE' | 'UNAVAILABLE';
export type InvestigationSynthesisChangeTypeDto =
  | 'WHAT_WE_KNOW_CHANGED'
  | 'POSSIBLE_CAUSE_STRENGTHENED'
  | 'POSSIBLE_CAUSE_WEAKENED'
  | 'OPEN_CHECK_ANSWERED'
  | 'NEW_CHECK_IDENTIFIED'
  | 'WHERE_THINGS_STAND_UPDATED'
  | 'CLOSER_TO_ACTION_OR_CLOSURE'
  | 'UNCERTAINTY_CHANGED';

export interface InvestigationSynthesisChangeDto {
  change_type: InvestigationSynthesisChangeTypeDto;
  message: string;
}

export interface InvestigationSynthesisDto {
  status: InvestigationSynthesisStatusDto;
  material_change: boolean;
  summary: string;
  changes: InvestigationSynthesisChangeDto[];
  uncertainty?: string | null;
  suggested_next_check?: string | null;
  assessment_updated: boolean;
  source_kind: string;
  source_id: string;
  reviewed_at?: string | null;
  source_user_id?: string | null;
  activity_id?: string | null;
}

export interface InvestigationParticipantProfileDto {
  user_id: string;
  display_name: string;
}

export interface InvestigationAccessDto {
  current_user_role: InvestigationRoleDto;
  is_participant: boolean;
  can_contribute: boolean;
  can_manage: boolean;
}

export interface InvestigationDetailResponseDto {
  schema_version: string;
  investigation: InvestigationDto;
  investigation_memory: InvestigationMemoryDto | null;
  pinned_evidence: PinnedEvidenceDto[];
  content: InvestigationContentDto[];
  activity: InvestigationActivityDto[];
  machine_knowledge?: MachineKnowledgeRecordDto[];
  participant_profiles?: InvestigationParticipantProfileDto[];
  latest_synthesis?: InvestigationSynthesisDto | null;
  access?: InvestigationAccessDto;
  counts: { evidence: number; content: number; activity: number; conversations: number; machine_knowledge?: number; active_machine_knowledge?: number };
  request_id?: string;
}

export interface CreateInvestigationResponseDto {
  schema_version: string;
  investigation: InvestigationDto;
  investigation_memory: InvestigationMemoryDto;
  shared_conversation: {
    conversation_id: string;
    investigation_id: string;
    machine_id: string;
    title?: string | null;
  };
  machine_name?: string | null;
  request_id?: string;
}

export function investigationStatusLabel(status: InvestigationStatusDto): string {
  if (status === 'CLOSED') return 'Completed';
  if (status === 'PAUSED') return 'On hold';
  if (status === 'ARCHIVED') return 'Archived';
  return 'Open';
}
